<template>
    <div class="creative-create">
        <div class="page-header">
            <h1 class="page-title">{{ $t('messages.creatives') }}</h1>
            <div class="header-actions" v-if="files.length > 0">
                <div class="file-count-info">
                    <span class="file-count">{{ files.length }} {{ $t('messages.files_selected') }}</span>
                    <div v-if="files.length >= MAX_FILES_LIMIT" class="file-limit-warning">
                        <img src="/images/info.svg" alt="warning" class="warning-icon" />
                        {{ $t('messages.max_files_reached', { limit: MAX_FILES_LIMIT }) }}
                    </div>
                </div>
                <div class="action-buttons">
                    <button @click="addMoreFiles" class="add-more-button" :disabled="files.length >= MAX_FILES_LIMIT">
                        <img src="/images/plus.svg" alt="add" class="add-icon" />
                        {{ $t('messages.add_more_files') }}
                    </button>
                    <button @click="clearAllFiles" class="clear-all-button">
                        <img src="/images/delete-white.svg" alt="delete" class="clear-icon" />
                        {{ $t('messages.clear_all') }}
                    </button>
                    <button @click="uploadFiles" class="upload-button" :disabled="files.length === 0">
                        {{ $t('messages.upload_button') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="files.length === 0" class="upload-section">
            <div class="upload-label" @dragover.prevent="handleDragOver" @dragenter.prevent="handleDragEnter"
                @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop" :class="{ 'drag-over': isDragOver }">
                <div class="upload-content">
                    <label for="upload-photo" class="upload-button">{{ $t('messages.choose_creatives') }}</label>
                    <p class="upload-hint">{{ $t('messages.drag_drop_hint') }}</p>
                </div>
                <div class="drop-zone" v-if="isDragOver">
                    <p>{{ $t('messages.drop_files_here') }}</p>
                    <small>{{ $t('messages.supported_formats') }}</small>
                </div>
                <input :key="inputKey" type="file" id="upload-photo" name="image" @change="getFiles" multiple
                    accept=".jpg,.jpeg,.png,.zip,.avi,.mov,.mp4,.mp3" />
            </div>
        </div>


        <div v-if="files.length !== 0" class="content">
            <table class="creative-create-table table" :data="files">
                <thead>
                    <tr>
                        <th class="name">{{ $t('messages.column_name') }}</th>
                        <th class="size">{{ $t('messages.column_size') }}</th>
                        <th class="link">{{ $t('messages.landing_page') }}</th>
                        <th class="tracking">{{ $t('messages.external_verifier') }}</th>
                        <th class="counter_impr">{{ $t('messages.impression_counter') }}</th>
                        <th class="counter_click">{{ $t('messages.click_counter') }}</th>
                        <th class="actions"></th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="row in rows" :key="row.name" :class="getRowClass(row)" :style="getRowStyle(row)">
                        <td class="name creative-create-name">
                            <div class="name-with-preview">
                                <template v-if="row.previewUrl">
                                    <img v-if="!row.isVideo" :src="row.previewUrl" alt="preview"
                                        class="preview-thumb-img" />
                                    <video v-else :src="row.previewUrl" muted playsinline
                                        class="preview-thumb-video"></video>
                                </template>
                                <img v-else-if="row.isZip" src="/images/html5.svg" alt="html5"
                                    class="preview-thumb-icon" />
                                <img v-else-if="row.isAudio" src="/images/audio.svg" alt="audio"
                                    class="preview-thumb-icon" />
                                <div class="name-size">
                                    <div class="file-name">{{ row.name }}</div>
                                    <div class="file-size">
                                        {{ formatFileSize(row.size) }}
                                        <div v-if="row._linkError || row._sizeError" class="row-error">
                                            {{ row._linkError || row._sizeError }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="size">
                            <select v-if="
                                row.type.includes('image') ||
                                row.type.includes('zip')
                            " v-model="row.dimension">
                                <option v-for="dimension in dimensions" :key="dimension.dimension"
                                    :value="dimension.dimension">
                                    {{ dimension.width }}x{{ dimension.height }}
                                </option>
                            </select>
                        </td>
                        <td class="link">
                            <textarea v-model="row.link" name="link" placeholder="https://"></textarea>
                        </td>
                        <td class="tracking">
                            <select name="tracking" v-model="row.tracking">
                                <option value="" selected>{{ $t('messages.no_verifier') }}</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                            </select>
                        </td>
                        <td class="counter_impr">
                            <textarea v-model="row.counter_impr" name="counter_impr" placeholder=""></textarea>
                        </td>
                        <td class="counter_click">
                            <textarea v-model="row.counter_click" name="counter_click" placeholder=""></textarea>
                        </td>
                        <td class="actions">
                            <div class="actions-content">

                                <label @click="removeFile(row)" class="delete-label"
                                    :class="{ disabled: row._status === 'uploading' }"
                                    v-tippy="{ content: $t('messages.remove_file'), theme: 'light' }"></label>
                                <label v-if="row._status === 'error'" @click="retryFile(row)" class="retry-label"
                                    v-tippy="{ content: $t('messages.retry'), theme: 'light' }"></label>
                                <label v-if="row._status === 'uploading'" @click="cancelFile(row)" class="cancel-label"
                                    v-tippy="{ content: $t('messages.cancel'), theme: 'light' }"></label>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "CreativeCreate",
    data() {
        return {
            files: [],
            startUpload: false,
            // Константы для валидации
            MAX_FILE_SIZE: 100 * 1024 * 1024, // 100MB в байтах
            MAX_FILES_LIMIT: 10, // Максимальное количество файлов
            isDragOver: false,
            // Поддерживаемые типы файлов
            SUPPORTED_FILE_TYPES: {
                images: ['jpg', 'jpeg', 'png'],
                videos: ['mp4', 'mov', 'avi'],
                audio: ['mp3'],
                archives: ['zip']
            },
            dimensions: [
                { dimension: "160x600", width: 160, height: 600 },
                { dimension: "240x400", width: 240, height: 400 },
                { dimension: "240x600", width: 240, height: 600 },
                { dimension: "250x250", width: 250, height: 250 },
                { dimension: "300x250", width: 300, height: 250 },
                { dimension: "300x600", width: 300, height: 600 },
                { dimension: "320x50", width: 320, height: 50 },
                { dimension: "320x100", width: 320, height: 100 },
                { dimension: "320x480", width: 320, height: 480 },
                { dimension: "336x280", width: 336, height: 280 },
                { dimension: "350x250", width: 350, height: 250 },
                { dimension: "468x60", width: 468, height: 60 },
                { dimension: "480x320", width: 480, height: 320 },
                { dimension: "728x90", width: 728, height: 90 },
                { dimension: "970x90", width: 970, height: 90 },
                { dimension: "970x250", width: 970, height: 250 },
                { dimension: "768x1024", width: 768, height: 1024 },
                { dimension: "1024x768", width: 1024, height: 768 },
            ],
            // управление последовательной загрузкой
            _currentUploadIndex: 0,
            inputKey: 0,
        };
    },
    computed: {
        hasFiles() {
            return this.files.length > 0;
        },
        rows() {
            return this.files;
        },
    },
    methods: {
        async getFiles() {
            const input = event.target;
            const files = Array.from((input && input.files) || []);

            // Валидация размера файлов
            const oversizedFiles = files.filter(file => file.size > this.MAX_FILE_SIZE);
            if (oversizedFiles.length > 0) {
                const fileNames = oversizedFiles.map(f => f.name).join(', ');
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: `${this.$t('messages.file_size_exceeded')}: ${fileNames}`,
                    type: 'error'
                });
                return;
            }

            // Валидация типов файлов
            const unsupportedFiles = files.filter(file => !this.resolveType(file));
            if (unsupportedFiles.length > 0) {
                const fileNames = unsupportedFiles.map(f => f.name).join(', ');
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: `${this.$t('messages.invalid_file_type')}: ${fileNames}`,
                    type: 'error'
                });
                return;
            }

            const allowed = this.dimensions.map(d => d.dimension);
            // Предпросмотр и автоподбор размера из списка для изображений; метки для видео и zip
            const withMeta = await Promise.all(
                files.map((f) => new Promise((resolve) => {
                    const ext = f.name.split('.').pop().toLowerCase();
                    const mime = (f.type || '').toLowerCase();
                    const isImage = mime.includes('image') || ['jpg', 'jpeg', 'png'].includes(ext);
                    const isVideo = mime.includes('video') || ['mp4', 'mov', 'avi'].includes(ext);
                    const isAudio = mime.includes('audio') || ['mp3', 'wav', 'ogg', 'aac'].includes(ext);
                    const isZip = ext === 'zip' || mime.includes('zip');

                    // инициализация статусов
                    f._status = 'pending';
                    f._progress = 0;
                    f._errorMessage = '';

                    if (isVideo) {
                        try { f.previewUrl = URL.createObjectURL(f); f.isVideo = true; } catch (e) { }
                        return resolve(f);
                    }
                    if (isZip) {
                        f.isZip = true;
                        return resolve(f);
                    }
                    if (isAudio) {
                        f.isAudio = true;
                        return resolve(f);
                    }
                    if (!isImage) {
                        return resolve(f);
                    }

                    const objectUrl = URL.createObjectURL(f);
                    const img = new Image();
                    img.onload = () => {
                        const detected = `${img.width}x${img.height}`;
                        if (allowed.includes(detected)) {
                            f.dimension = detected;
                        }
                        try { f.previewUrl = objectUrl; } catch (e) { }
                        resolve(f);
                    };
                    img.onerror = () => {
                        try { URL.revokeObjectURL(objectUrl); } catch (e) { }
                        resolve(f);
                    };
                    img.src = objectUrl;
                }))
            );
            this.files = [...this.files, ...withMeta];
            // перерисовать input, чтобы снова выбрать тот же файл
            this.inputKey++;
        },
        resolveType(file) {
            const mime = (file.type || '').toLowerCase();
            const ext = (file.name.split('.').pop() || '').toLowerCase();

            // Проверяем по расширению файла
            if (this.SUPPORTED_FILE_TYPES.images.includes(ext)) {
                return 'image';
            }
            if (this.SUPPORTED_FILE_TYPES.videos.includes(ext)) {
                return 'video';
            }
            if (this.SUPPORTED_FILE_TYPES.audio.includes(ext)) {
                return 'audio';
            }
            if (this.SUPPORTED_FILE_TYPES.archives.includes(ext)) {
                return 'archive';
            }

            // Дополнительная проверка по MIME типу
            if (mime.includes('image')) return 'image';
            if (mime.includes('video')) return 'video';
            if (mime.includes('audio')) return 'audio';
            if (mime.includes('zip') || mime.includes('application/zip')) return 'archive';

            return null; // Неподдерживаемый тип файла
        },
        isValidUrl(value) {
            if (!value) return false;
            try {
                const u = new URL(value);
                return ['http:', 'https:'].includes(u.protocol);
            } catch (_) {
                return false;
            }
        },
        async uploadFiles() {
            this.startUpload = true;
            this._currentUploadIndex = 0;

            // Запустить последовательную загрузку
            this._processSequentialUpload();
        },
        /**
         * Загружает файл на сервер
         * @param {File} item - файл для загрузки
         * @param {string} type - тип файла (image, video, audio, archive)
         */
        async uploadFile(item, type) {
            const form = new FormData();

            // Определение размеров в зависимости от типа файла
            let width = 0;
            let height = 0;
            let sizeLabel = null;

            if (type === "image") {
                if (item.dimension && /^(\d+)x(\d+)$/.test(item.dimension)) {
                    const [w, h] = item.dimension.split('x').map(Number);
                    width = w; height = h; sizeLabel = item.dimension;
                } else {
                    const dim = this.dimensions.find((o) => o.dimension === item.dimension);
                    if (dim) { width = dim.width; height = dim.height; sizeLabel = dim.dimension; }
                }
            } else if (type === "archive") {
                const dim = this.dimensions.find((o) => o.dimension === item.dimension);
                if (dim) { width = dim.width; height = dim.height; sizeLabel = dim.dimension; }
            } else if (type === "video") {
                width = 400; height = 300;
            } else if (type === "audio") {
                width = 300; height = 150;
            }

            form.append("type", type.toLowerCase());
            form.append("size", sizeLabel);
            form.append("width", width);
            form.append("height", height);
            form.append("image", item);
            form.append("name", item.name.substr(0, item.name.lastIndexOf(".")));
            form.append("link", item.link || "");
            form.append("tracking", item.tracking || "");
            form.append("counter_impr", item.counter_impr || "");
            form.append("counter_click", item.counter_click || "");
            form.append("extension", item.name.split(".").pop().toLowerCase());

            try {
                item._status = 'uploading';
                item._progress = 0;
                this.$forceUpdate(); // Обновляем Vue для отображения статуса uploading

                // AbortController для отмены
                const controller = new AbortController();
                item._abortController = controller;

                const response = await axios.post("/creatives/upload", form, {
                    signal: controller.signal,
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent && progressEvent.total) {
                            item._progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            this.$forceUpdate(); // Обновляем Vue при изменении прогресса
                        }
                    },
                });

                item._progress = 100;
                item._status = 'done';
                item._abortController = null;
                this.$forceUpdate(); // Обновляем Vue для отображения статуса done

                // Переходим к следующему файлу
                this._currentUploadIndex++;
                this._processSequentialUpload();
            } catch (error) {
                if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.message === 'canceled') {
                    item._status = 'pending';
                    item._progress = 0;
                    item._errorMessage = '';
                    this.$forceUpdate(); // Обновляем Vue для отмены
                } else {
                    item._status = 'error';

                    // Детальная обработка различных типов ошибок
                    let errorMessage = this.$t('messages.failed_to_upload_creative');

                    if (error.response) {
                        // Ошибка от сервера
                        const status = error.response.status;
                        const serverMessage = error.response.data?.message;

                        switch (status) {
                            case 400:
                                errorMessage = serverMessage || this.$t('messages.validation_error');
                                break;
                            case 401:
                                errorMessage = this.$t('messages.unauthorized_error');
                                break;
                            case 403:
                                errorMessage = this.$t('messages.forbidden_error');
                                break;
                            case 413:
                                errorMessage = this.$t('messages.file_size_exceeded');
                                break;
                            case 500:
                                errorMessage = this.$t('messages.server_error');
                                break;
                            default:
                                errorMessage = serverMessage || `${this.$t('messages.server_error')} (${status})`;
                        }
                    } else if (error.request) {
                        // Ошибка сети
                        errorMessage = this.$t('messages.network_error');
                    } else if (error.code === 'ECONNABORTED') {
                        // Таймаут
                        errorMessage = this.$t('messages.timeout_error');
                    }

                    item._errorMessage = `${errorMessage}: ${item.name}`;
                    this.$notify({
                        title: this.$t('messages.error_occurred'),
                        message: item._errorMessage,
                        type: 'error'
                    });
                    this.$forceUpdate(); // Обновляем Vue для отображения ошибки
                }
                item._abortController = null;

                // Переходим к следующему файлу даже при ошибке
                this._currentUploadIndex++;
                this._processSequentialUpload();
            }
        },
        retryFile(fileToRetry) {
            // Очищаем все ошибки
            fileToRetry._linkError = '';
            fileToRetry._sizeError = '';
            fileToRetry._status = 'pending';
            fileToRetry._errorMessage = '';

            // Найти индекс файла и установить его как следующий для загрузки
            const fileIndex = this.files.indexOf(fileToRetry);
            if (fileIndex !== -1) {
                this._currentUploadIndex = fileIndex;
                this._processSequentialUpload();
            }
        },
        removeFile(fileToRemove) {
            // Не удалять файл, если он загружается
            if (fileToRemove._status === 'uploading') {
                return;
            }

            // Отменить загрузку если файл загружается
            if (fileToRemove._abortController) {
                try {
                    fileToRemove._abortController.abort();
                } catch (e) {
                }
                fileToRemove._abortController = null;
            }

            // Удалить из списка файлов
            this.files = this.files.filter((f) => f !== fileToRemove);

            // Освободить blob URL для предотвращения утечек памяти
            if (fileToRemove.previewUrl) {
                try {
                    URL.revokeObjectURL(fileToRemove.previewUrl);
                } catch (e) {
                }
            }

            // Если все файлы удалены, сбросить состояние загрузки
            if (this.files.length === 0) {
                this.startUpload = false;
                this._currentUploadIndex = 0;
            } else {
                // Если удалили файл, который был текущим для загрузки, корректируем индекс
                if (this._currentUploadIndex >= this.files.length) {
                    this._currentUploadIndex = 0;
                    this._processSequentialUpload();
                }
            }

            // Перерисовать input для возможности повторного выбора того же файла
            this.inputKey++;
        },
        cancelFile(row) {
            if (row._abortController) {
                try { row._abortController.abort(); } catch (e) { }
                row._abortController = null;
            }
        },
        _processSequentialUpload() {
            // Проверяем, есть ли еще файлы для загрузки
            if (this._currentUploadIndex >= this.files.length) {
                // Все файлы обработаны
                this.startUpload = false;
                this._currentUploadIndex = 0;

                const successCount = this.files.filter(f => f._status === 'done').length;
                const errorCount = this.files.filter(f => f._status === 'error').length;

                this.$notify({
                    title: this.$t('messages.completed_successfully'),
                    message: `${successCount} ${this.$t('messages.files_uploaded')}, ${errorCount} ${this.$t('messages.files_failed')}`,
                    type: successCount > 0 ? 'success' : 'warning'
                });
                return;
            }

            const currentFile = this.files[this._currentUploadIndex];

            // Пропускаем уже обработанные файлы
            if (currentFile._status === 'done' || currentFile._status === 'error') {
                this._currentUploadIndex++;
                this._processSequentialUpload();
                return;
            }

            // Валидация файла
            const validationResult = this._validateFileForUpload(currentFile);
            if (!validationResult.isValid) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: validationResult.errorMessage,
                    type: 'error'
                });
                currentFile._status = 'error';
                currentFile._errorMessage = validationResult.errorMessage;
                this._currentUploadIndex++;
                this._processSequentialUpload();
                return;
            }

            // Загружаем файл
            this.uploadFile(currentFile, validationResult.type);
        },

        _validateFileForUpload(item) {
            const type = this.resolveType(item);

            if (!type) {
                return {
                    isValid: false,
                    errorMessage: this.$t('messages.invalid_file_type')
                };
            }

            const isImage = type === 'image';
            const isVideo = type === 'video';
            const isAudio = type === 'audio';
            const isZip = type === 'archive';

            // Очищаем предыдущие ошибки
            item._linkError = '';
            item._sizeError = '';

            // Проверяем URL
            if (!this.isValidUrl(item.link)) {
                item._linkError = this.$t('messages.invalid_url_format');
                return {
                    isValid: false,
                    errorMessage: `${this.$t('messages.invalid_url_format')}: ${item.name}`
                };
            }

            // Проверяем размер файла для изображений и zip
            if ((isImage || isZip) && !item.dimension) {
                item._sizeError = this.$t('messages.size_not_selected');
                return {
                    isValid: false,
                    errorMessage: `${this.$t('messages.size_not_selected')}: ${item.name}`
                };
            }

            // Проверяем обязательные поля
            if (((isImage || isZip) && !item.link) || ((isAudio || isVideo) && !item.link)) {
                return {
                    isValid: false,
                    errorMessage: this.$t('messages.fill_required_fields')
                };
            }

            return {
                isValid: true,
                type: type
            };
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        getRowClass(file) {
            const classes = [];
            if (file._status === 'uploading') classes.push('uploading');
            if (file._status === 'error') classes.push('error');
            if (file._status === 'done') classes.push('success');
            if (file._linkError || file._sizeError) classes.push('has-error');

            return classes.join(' ');
        },
        getRowStyle(file) {
            if (file._status === 'uploading' && file._progress !== undefined) {
                return {
                    'background': `linear-gradient(90deg, rgba(0, 166, 255, 0.3) 0%, rgba(0, 166, 255, 0.1) 100%)`,
                    'background-size': `${file._progress || 0}% 100%`,
                    'background-repeat': 'no-repeat'
                };
            } else if (file._status === 'error') {
                return {
                    'background': 'rgba(239, 68, 68, 0.2) !important'
                };
            }
            return {};
        },
        handleDragOver(event) {
            event.preventDefault();
            this.isDragOver = true;
        },
        handleDragEnter(event) {
            event.preventDefault();
            this.isDragOver = true;
        },
        handleDragLeave(event) {
            event.preventDefault();
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isDragOver = false;
            }
        },
        handleDrop(event) {
            event.preventDefault();
            this.isDragOver = false;

            const files = Array.from(event.dataTransfer.files);
            if (files.length > 0) {
                const fakeEvent = {
                    target: {
                        files: files
                    }
                };
                this.getFiles(fakeEvent);

                // Показываем уведомление о успешном перетаскивании
                this.$notify({
                    title: this.$t('messages.success'),
                    message: `${files.length} ${this.$t('messages.files_dropped')}`,
                    type: 'success'
                });
            }
        },

        /**
         * Добавляет дополнительные файлы к уже выбранным
         */
        addMoreFiles() {
            // Проверяем лимит файлов
            if (this.files.length >= this.MAX_FILES_LIMIT) {
                this.$notify({
                    title: this.$t('messages.warning'),
                    message: this.$t('messages.max_files_reached', { limit: this.MAX_FILES_LIMIT }),
                    type: 'warning'
                });
                return;
            }

            // Создаем новый input элемент для выбора файлов
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = '.jpg,.jpeg,.png,.zip,.avi,.mov,.mp4,.mp3';

            input.onchange = (event) => {
                const files = Array.from(event.target.files);
                if (files.length > 0) {
                    // Проверяем, не превысим ли лимит
                    const totalFiles = this.files.length + files.length;
                    if (totalFiles > this.MAX_FILES_LIMIT) {
                        this.$notify({
                            title: this.$t('messages.warning'),
                            message: this.$t('messages.would_exceed_limit', {
                                current: this.files.length,
                                trying: files.length,
                                limit: this.MAX_FILES_LIMIT
                            }),
                            type: 'warning'
                        });
                        return;
                    }

                    // Создаем искусственное событие для переиспользования существующей логики
                    const fakeEvent = {
                        target: {
                            files: files
                        }
                    };
                    this.getFiles(fakeEvent);

                    // Показываем уведомление о добавлении файлов
                    this.$notify({
                        title: this.$t('messages.success'),
                        message: `${files.length} ${this.$t('messages.files_added')}`,
                        type: 'success'
                    });
                }
            };

            input.click();
        },


        /**
         * Очищает все выбранные файлы
         */
        clearAllFiles() {
            this.$confirm(
                this.$t('messages.confirm_clear_all_files'),
                this.$t('messages.warning'),
                {
                    confirmButtonText: this.$t('messages.clear_all'),
                    cancelButtonText: this.$t('messages.cancel'),
                    type: 'warning',
                    confirmButtonClass: 'el-button--danger'
                }
            ).then(() => {
                // Освобождаем все blob URL
                this.files.forEach(file => {
                    if (file.previewUrl) {
                        URL.revokeObjectURL(file.previewUrl);
                    }
                });

                // Очищаем массивы
                this.files = [];
                this.startUpload = false;
                this._currentUploadIndex = 0;

                // Обновляем ключ input для сброса состояния
                this.inputKey++;

                this.$notify({
                    title: this.$t('messages.success'),
                    message: this.$t('messages.all_files_cleared'),
                    type: 'success'
                });
            }).catch(() => {
                // Пользователь отменил очистку
            });
        },
    },
    beforeDestroy() {
        // Освободить все blob URL при уничтожении компонента
        this.files.forEach(file => {
            if (file.previewUrl) {
                try {
                    URL.revokeObjectURL(file.previewUrl);
                } catch (e) {
                }
            }
        });

        // Отменить все активные загрузки
        this.files.forEach(file => {
            if (file._abortController) {
                try {
                    file._abortController.abort();
                } catch (e) {
                }
            }
        });
    },
};
</script>
